import { makeAutoObservable } from 'mobx'
import { ContestantWithBio, ICategory, ICompetition, ICompetitionStaff, IPayoutOptions, IUserPayload, SaveStaffRequest, Status } from '../utils/interfaces'
import cloneDeep from 'lodash/cloneDeep'
import votingRoundStore from './votingRoundStore'
import { TTab } from '../pages/CompetitionDashboard/components/TabContent'
import uiStore from './uiStore'
import seasonStore from './seasonStore'
import contestantStore from './contestantStore'
import { COMPETITION_BANNER_S3_FOLDER, COMPETITION_LOGO_S3_FOLDER, status, seasonTypes } from '../utils/constants'
import { getCompetitionUsername, isEmail, isPhoneNo, retrieveErrorMessage, uploadImage } from '../utils/helpers'
import { createCategoriesRequest, deleteCategoriesRequest, deleteCompetitionRequest, deleteCompetitionStaffRequest, fetchCompetitionDetailsRequest, getCompetitionStaffRequest, saveCompetitionStaffRequest, savePayoutOptionsRequest, setCompetitionSubscriptionPlanRequest, setCompetitionTemplateRequest, setSeasonPaymentRequest, updateCategoryRequest, updateCompetitionRequest } from '../utils/apiRequests'
import authStore from './authStore'
import competitionStore from './competitionStore'

type TCompetitionDashboardData = {
  competitionDetails: ICompetition
  editField: string
  editValue: string
  editInputError: string
  contestantAvatar: string
  status: TStatus
  errorMessages: TErrorMessage
  selectedTab: string
  showAddContestantForm: boolean
  competitionStaff: ICompetitionStaff[]
  selectedCategory: ICategory
  allCategories: ICategory[]
  notPermitted: boolean
  competitionDashboardStatus: Status
}

type TStatus = {
  fetchDetails?: Status
  updateCompetition?: Status
  loadingDashboardData?: Status
  fetchingCompetitionStaff?: Status
  savePayoutOptions?: Status
  saveCompetitionStaff?: Status
}

type TErrorMessage = {
  fetchDetails?: string
  updateCompetition?: string
  loadingDashboardData?: string
  fetchingCompetitionStaff?: string
  savePayoutOptions?: string
  saveCompetitionStaff?: string
}

type TSetSubscriptionPlan = {
  competitionUsername: string,
  subscriptionPlan: number,
  reference?: string,
  amount?: number,
  seasonId?: string,
}

type TCompetitionDetailsKey = "name" | "description" | "logo" | "banner"
type FilterKey = 'username' | 'email' | 'phone_number'

export const CONTESTANT_TAB = 'Contestants'
export const OPTIONS_TAB = 'Options'
export const CATEGORIES_TAB = 'Categories'

const defaultData: TCompetitionDashboardData = {
  competitionDetails: {} as ICompetition,
  editField: '',
  editValue: '',
  editInputError: '',
  contestantAvatar: '',
  selectedTab: CONTESTANT_TAB,
  competitionStaff: [],
  selectedCategory: {} as ICategory,
  allCategories: [],
  notPermitted: false,
  competitionDashboardStatus: null,
  status: {
    fetchDetails: undefined,
    updateCompetition: undefined,
    loadingDashboardData: undefined,
    fetchingCompetitionStaff: undefined,
    savePayoutOptions: undefined,
    saveCompetitionStaff: undefined
  },
  errorMessages: {
    fetchDetails: '',
    loadingDashboardData: '',
    fetchingCompetitionStaff: '',
    savePayoutOptions: '',
    saveCompetitionStaff: ''
  },
  showAddContestantForm: false
}

class CompetitionDashboardStore {
  data: TCompetitionDashboardData = cloneDeep(defaultData)

  constructor() {
    makeAutoObservable(this)
  }

  closeSeason = (seasonId: string) => {
    uiStore.openConfirmDialog('Are you sure you want to end this season?', () => {
      seasonStore.closeSeason(seasonId)
    })
  }

  onCloseEditModal = () => {
    this.data.editField = ''
    this.data.editValue = ''
    this.data.editInputError = ''
  }

  updateFields = (field?: TCompetitionDetailsKey, value?: string) => {
    if (!this.data.editValue && !value) {
      this.data.editInputError = "This field cannot be blank"
      return
    }

    const payload: ICompetition = {...this.data.competitionDetails}
    payload[field || this.data.editField as TCompetitionDetailsKey] = value || this.data.editValue

    this.updateCompetition(payload)
    this.onCloseEditModal()
  }

  onEditName = () => {
    this.data.editInputError = ''
    this.data.editField = 'name'
    this.data.editValue = this.data.competitionDetails.name
  }

  onEditDescription = () => {
    this.data.editInputError = ''
    this.data.editField = 'description'
    this.data.editValue = seasonStore.selectedSeason?.description || ''
  }

  deleteContestant = (contestantUsername: string) => {
    uiStore.openConfirmDialog("Are you sure you want to delete this contestant", () => {
      this.deleteContestantRequest(this.seasonId, contestantUsername)
    })
  }

  deleteContestantRequest = (seasonId: string, contestantUsername: string) => {
    contestantStore.deleteContestant(seasonId, contestantUsername, false)  
  }

  evictContestant = (contestantUsername: string) => {
    uiStore.openConfirmDialog("Are you sure you want to evict this contestant", () => {
      contestantStore.evictContestant(this.seasonId, contestantUsername)
    })
  }

  setEditValue = (value: string) => {
    this.data.editValue = value
  }

  clearData = () => {
    this.data = cloneDeep(defaultData)
  }

  saveContestant = (payload: ContestantWithBio) => {
    if (!this.selectedCategory) return uiStore.errorModal('Please select a category to add a contestant.');
    contestantStore.saveContestant({
      payload,
      creatingCompetition: false,
      currentSeasonId: this.seasonId,
      subscriptionPlan: this.subscriptionPlan,
      categoryId: this.selectedCategory?.id
    })
  }

  setUpdating = () => {
    this.data.status.updateCompetition = status.LOADING
    this.data.errorMessages.updateCompetition = ''
  }

  prepareLogo = async (payload: Partial<ICompetition>) => {
    payload.logo = await uploadImage({
      key: this.username,
      imageUrl: payload.logo,
      folderPath: COMPETITION_LOGO_S3_FOLDER,
      imageElementsToRefresh: document.getElementsByClassName("competition-logo")
    })
  }

  prepareBanner = async (payload: Partial<ICompetition>) => {
    payload.banner = await uploadImage({
      key: this.username,
      imageUrl: payload.banner,
      folderPath: COMPETITION_BANNER_S3_FOLDER,
      imageElementsToRefresh: document.getElementsByClassName("competition-banner")
    })
  }

  onUpdateFailed = (e: any) => {
    this.data.errorMessages.updateCompetition = retrieveErrorMessage(e)
    this.data.status.updateCompetition = status.FAILED
    uiStore.errorModal(this.data.errorMessages.updateCompetition)
  }

  clearInputErrors = () => {
    this.data.editInputError = ''
  }

  setSelectedTab = (tab: string) => {
    this.data.selectedTab = tab
  }

  setContestantAvatar = (avatar: string) => {
    this.data.contestantAvatar = avatar
  }

  setShowAddContestantForm = (show: boolean) => {
    this.data.showAddContestantForm = show
  }

  onClickContestantButton = () => {
    if (!this.canAddContestant) {
      uiStore.errorModal('You have exceeded the maximum number of contestants, please upgrade your account plan.')
      return
    }
    this.setShowAddContestantForm(true)
  }

  onAddContestant = (payload: ContestantWithBio, onComplete?: () => void) => {
    payload.avatar = competitionDashboardStore.contestantAvatar
    contestantStore.saveContestant({
      payload,
      creatingCompetition: false,
      currentSeasonId: this.seasonId,
      subscriptionPlan: this.subscriptionPlan,
      categoryId: this.selectedCategory?.id
    }).then(() => {
      onComplete && onComplete()
      this.setShowAddContestantForm(false)
    })
  }

  setLoadingDashboardData = () => {
    this.data.status.loadingDashboardData = status.LOADING
    this.data.notPermitted = false
    this.data.errorMessages.loadingDashboardData = ''
    uiStore.setLoadingScreenMessage('Loading Competition Dashboard.')
  }

  setFetchingCompetitionStaff = () => {
    this.data.status.fetchingCompetitionStaff = status.LOADING
    this.data.errorMessages.fetchingCompetitionStaff = ''
  }

  setFetchingCompetitionStaffComplete = (data: ICompetitionStaff[]) => {
    this.data.status.fetchingCompetitionStaff = status.COMPLETE
    this.data.competitionStaff = data
    this.data.errorMessages.fetchingCompetitionStaff = ''
  }

  setFetchingCompetitionStaffFailed = (error: unknown) => {
    this.data.status.fetchingCompetitionStaff = status.FAILED
    this.data.errorMessages.fetchingCompetitionStaff = retrieveErrorMessage(error)
  }

  setCompetitionCategory = (category: ICategory) => {
    this.data.selectedCategory = category
  }

  loadDashboardDataComplete = (data: ICompetition) => {
    this.data.status.loadingDashboardData = status.COMPLETE
    this.data.competitionDetails = data
    this.data.errorMessages.loadingDashboardData = ''
    this.data.competitionDashboardStatus = status.COMPLETE

    seasonStore.setSeasons(data.seasons)
    seasonStore.setSelectedSeason(seasonStore.latestSeason)
    const nonDefaultCategory = data.categories?.find(e => e.is_default)
    if (nonDefaultCategory) this.data.selectedCategory = nonDefaultCategory
    this.data.allCategories = data.categories || []
  }

  loadDashboardDataFailed = (error: any) => {
    const errMessage = retrieveErrorMessage(error)
    if ((errMessage.includes('not') && errMessage.includes('permission')) || error?.response?.status === 403) {
      this.data.notPermitted = true
    }
    this.data.status.loadingDashboardData = status.FAILED
    this.data.errorMessages.loadingDashboardData = retrieveErrorMessage(error)
  }

  updateCompetitionSuccess = (data: ICompetition) => {
    this.data.competitionDetails = data
    this.data.status.updateCompetition = status.COMPLETE
    this.data.errorMessages.updateCompetition = ''

    seasonStore.setSeasons(data.seasons)
    seasonStore.setSelectedSeasonById(this.seasonId)
  }

  setDashboardDetails = (details: Partial<ICompetition>) => {
    this.data.competitionDetails = {
      ...this.data.competitionDetails,
      ...details
    }
  }

  createCategorySuccess = (data: ICategory[], isUpdate: boolean = false) => {
    this.data.allCategories = [...data]
    uiStore.showToastMessage({type: 'success', message: isUpdate ? 'category updated' : 'category created'})
  }

  deleteCategorySuccess = (categoryId: number) => {
    this.data.allCategories = this.data.allCategories.filter(e => e.id !== categoryId)
    uiStore.showToastMessage({type: 'success', message: 'category deleted'})
  }

  savingPayoutOptions = () => {
    this.data.status.savePayoutOptions = status.LOADING
    this.data.errorMessages.savePayoutOptions = ''
  }

  savePayoutOptionsSuccess = (payload: IPayoutOptions) => {
    this.data.status.savePayoutOptions = status.COMPLETE
    this.data.competitionDetails.payout_options = payload
    this.data.errorMessages.savePayoutOptions = ''
    uiStore.successModal('Payout options saved!')
  }

  savePayoutOptionsFailed = (error: unknown) => {
    this.data.status.savePayoutOptions = status.FAILED
    this.data.errorMessages.savePayoutOptions = retrieveErrorMessage(error)
    uiStore.errorModal(this.data.errorMessages.savePayoutOptions)
  }

  savingCompetitionStaff = () => {
    this.data.status.saveCompetitionStaff = status.LOADING
    this.data.errorMessages.saveCompetitionStaff = ''
  }

  saveCompetitionStaffSuccess = (data: ICompetitionStaff) => {
    this.data.status.saveCompetitionStaff = status.COMPLETE
    this.data.competitionStaff = this.data.competitionStaff.concat(data)
    this.data.errorMessages.saveCompetitionStaff = ''
  }

  saveCompetitionStaffFailed = (error: unknown) => {
    this.data.status.saveCompetitionStaff = status.FAILED
    this.data.errorMessages.saveCompetitionStaff = retrieveErrorMessage(error)
    uiStore.errorModal(this.data.errorMessages.saveCompetitionStaff)
  }

  toggleArchiveSuccess = (data: ICompetition) => {
    this.data.competitionDetails = data
    uiStore.successModal('Update successful')
    uiStore.setLoadingScreenMessage('')
  }

  deleteCompetitionStaffSuccess = (username: string) => {
    let filterKey: FilterKey  = 'username'

    if (isPhoneNo(username)) {
      filterKey = 'phone_number'
    } else if (isEmail(username)) {
      filterKey = 'email'
    } else {
      filterKey = 'username'
    }

    this.data.competitionStaff = this.data.competitionStaff.filter(staff => staff.personnel[filterKey as keyof IUserPayload] !== username)
  }

  setCompetitionTemplateSuccess = (template: number) => {
    this.data.competitionDetails.template = template
  }

  setSubscriptionInfo = (plan: number) => {
    this.data.competitionDetails.pricing_subscription = plan
    this.data.competitionDetails.paid = true
  }

  async setCompetitionTemplate(template: number) {
    setCompetitionTemplateRequest(this.username, template)
    this.setCompetitionTemplateSuccess(template)
  }

  async deleteCompetition(competitionUsername: string, onDelete?: () => void) {
    uiStore.setLoadingScreenMessage('Deleting competition...')

    try {
      await deleteCompetitionRequest(competitionUsername)
      if (onDelete) onDelete()
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
    }

    uiStore.setLoadingScreenMessage('')
  }

  async deleteCompetitionStaff(competitionUsername: string, username: string) {
    uiStore.setLoadingScreenMessage('Removing user...')

    try {
      await deleteCompetitionStaffRequest(competitionUsername, { email: username })
      this.deleteCompetitionStaffSuccess(username)
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
    }
    uiStore.setLoadingScreenMessage('')
  }

  async savePayoutOptions(username: string, payload: IPayoutOptions) {
    if (this.savingPayoutOptionsLoading) return 
    this.savingPayoutOptions()

    try {
      await savePayoutOptionsRequest(username, payload)
      this.savePayoutOptionsSuccess(payload)
    } catch (e) {
      this.savePayoutOptionsFailed(e)
    }
  }

  async saveCompetitionStaff(username: string, payload: SaveStaffRequest) {
    if (this.savingCompetitionStaffLoading) return 
    this.savingCompetitionStaff()

    try {
      const response = await saveCompetitionStaffRequest(username, payload)
      this.saveCompetitionStaffSuccess(response.data)
    } catch (e) {
      this.saveCompetitionStaffFailed(e)
    }
  }

  async toggleArchive() {
    const competitionDetails = {...this.data.competitionDetails}
    uiStore.setLoadingScreenMessage(`${competitionDetails.archived ? 'Un-archiving ' : 'Archiving '} competition...`)

    try {
      competitionDetails.archived = !competitionDetails.archived
      const response = await updateCompetitionRequest(competitionDetails, competitionDetails.username)
      this.toggleArchiveSuccess(response.data)
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
    }

    uiStore.setLoadingScreenMessage('')
  }

  async createCategory(name: string, isDefault: boolean = false) {
    try {
      const response = await createCategoriesRequest(this.username, { name, is_default: isDefault })
      this.createCategorySuccess(response.data.data)
    } catch (e) {
      uiStore.showToastMessage({type: 'error', message: retrieveErrorMessage(e)})
    }
  }

  async deleteCategory(categoryId: number) {
    try {
      await deleteCategoriesRequest(this.username, categoryId)
      this.deleteCategorySuccess(categoryId)
    } catch (e) {
      uiStore.showToastMessage({type: 'error', message: retrieveErrorMessage(e)})
    }
  }

  async updateCategory(categoryId: number, name: string, isDefault: boolean = false) {
    try {
      const response = await updateCategoryRequest(this.username, { id: categoryId, name, is_default: isDefault })
      this.createCategorySuccess(response.data.data, true)
    } catch (e) {
      uiStore.showToastMessage({type: 'error', message: retrieveErrorMessage(e)})
    }
  }

  async fetchCompetitionStaff(username?: string) {
    if (this.fetchingCompetitionStaff) return
    this.setFetchingCompetitionStaff()

    try {
      const competitionUsername = username || getCompetitionUsername()
      if (!competitionUsername) return

      const response = await getCompetitionStaffRequest(competitionUsername)
      this.setFetchingCompetitionStaffComplete(response.data)
    } catch (e) {
      this.setFetchingCompetitionStaffFailed(e)
    }
  }

  async updateCompetition(payload: ICompetition) {
    if (this.updateCompetitionLoading) return
    this.setUpdating()

    try {
      if (payload.logo && (this.logo !== payload.logo)) await this.prepareLogo(payload)
      if (payload.banner && (this.banner !== payload.banner)) await this.prepareBanner(payload)
  
      const response = await updateCompetitionRequest(payload, this.username, this.seasonId)
      this.updateCompetitionSuccess(response.data)
    } catch (e) {
      this.onUpdateFailed(e)
    }
  }

  async loadDashboardData() {
    if (this.loadingDashboardData) return
    this.setLoadingDashboardData()
    
    try {
      const competitionUsername = getCompetitionUsername()
      if (!competitionUsername) return

      const [competitionDashboardResponse,] = await Promise.all([
        fetchCompetitionDetailsRequest(competitionUsername),
        this.fetchCompetitionStaff(competitionUsername)
      ])

      this.loadDashboardDataComplete(competitionDashboardResponse.data)
    } catch (e) {
      this.loadDashboardDataFailed(e)
    }

    uiStore.setLoadingScreenMessage('')
  }

  async updateSubscriptionPlan({
    competitionUsername,
    subscriptionPlan,
    reference,
    amount,
    seasonId
  }: TSetSubscriptionPlan) {
    uiStore.setLoadingScreenMessage('Processing...')
    try {
      if (amount && seasonId && reference) {
        await setSeasonPaymentRequest(seasonId, { amount, reference })
      }

      const response = await setCompetitionSubscriptionPlanRequest({
        competition_id: competitionUsername,
        pricing_subscription: subscriptionPlan
      })

      this.setSubscriptionInfo(subscriptionPlan)
      uiStore.successModal(response.data.message)
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
    }

    uiStore.setLoadingScreenMessage('')
  }

  get tabs() {
    const sectionTabs: TTab = {CONTESTANT_TAB, CATEGORIES_TAB}
    if (!!this.votingOptions) sectionTabs.OPTIONS_TAB = OPTIONS_TAB
    return sectionTabs
  }

  get savingCompetitionStaffLoading() {
    return this.data.status.saveCompetitionStaff === status.LOADING
  }

  get savingPayoutOptionsLoading() {
    return this.data.status.savePayoutOptions === status.LOADING
  }

  get tabLabels() {
    const defaultLabels = [CONTESTANT_TAB, CATEGORIES_TAB]
    if (!!this.votingOptions) defaultLabels.push(OPTIONS_TAB)
    return defaultLabels
  }

  get votingOptions() {
    return votingRoundStore.lastVotingRound?.voting_option
  }

  get seasonId() {
    return seasonStore.selectedSeason?.uuid
  }

  get updateCompetitionLoading() {
    return this.data.status.updateCompetition === status.LOADING
  }

  get updateCompetitionComplete() {
    return this.data.status.updateCompetition === status.COMPLETE
  }

  get updateCompetitionFailed() {
    return this.data.status.updateCompetition === status.FAILED
  }

  get updateCompetitionError() {
    return this.data.errorMessages.updateCompetition
  }

  get logo() {
    return this.data.competitionDetails.logo
  }

  get banner() {
    return this.data.competitionDetails.banner
  }

  get name() {
    return this.data.competitionDetails.name
  }

  get username() {
    return this.data.competitionDetails.username
  }

  get selectedTab() {
    return this.data.selectedTab
  }

  get contestantAvatar() {
    return this.data.contestantAvatar
  }

  get editField() {
    return this.data.editField
  }

  get editValue() {
    return this.data.editValue
  }

  get editInputError() {
    return this.data.editInputError
  }

  get showAddContestantForm() {
    return this.data.showAddContestantForm
  }

  get canAddContestant() {
    const subscriptionPlan = this.data.competitionDetails.pricing_subscription|| seasonTypes.FREE
    if (!subscriptionPlan && subscriptionPlan !== 0) return false
    if (this.isPremierSubscriptionPlan || this.isProPlan) return true

    const options = competitionStore.getPlanByCode(subscriptionPlan)
    return contestantStore.contestants.length < (options?.max_contestants || 0)
  }

  get subscriptionPlan() {
    return this.data.competitionDetails.pricing_subscription || seasonTypes.FREE
  }

  get isPremierSubscriptionPlan() {
    return this.data.competitionDetails.pricing_subscription === seasonTypes.PREMIER
  }

  get isProPlan() {
    return this.data.competitionDetails.pricing_subscription === seasonTypes.PRO
  }

  get loadingDashboardData() {
    return this.data.status.loadingDashboardData === status.LOADING
  }

  get fetchingCompetitionStaff() {
    return this.data.status.fetchingCompetitionStaff === status.LOADING
  }

  get fetchingCompetitionStaffComplete() {
    return this.data.status.fetchingCompetitionStaff === status.COMPLETE
  }

  get fetchingCompetitionStaffFailed() {
    return this.data.status.fetchingCompetitionStaff === status.FAILED
  }

  get selectedCategory() {
    return this.data.selectedCategory
  }

  get allCategories() {
    return this.data.allCategories
  }

  get dashboardLoaded() {
    return this.data.competitionDashboardStatus === status.COMPLETE
  }

  get currentStaff() {
    const staffEmail = authStore.userEmail
    if (!staffEmail) return null

    return this.data.competitionStaff.find(c => c.personnel.email === staffEmail)
  }

  get currentStaffRole() {
    return this.currentStaff?.role || 0
  }

  get isCompetitionPublic() {
    return this.data.competitionDetails.visibility === "public_visibility"
  }
}

const competitionDashboardStore = new CompetitionDashboardStore()
export default competitionDashboardStore